import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import { useTenantConfig } from '../../../hooks/use-tenant-config';
import { passingText } from '../../../lib/grading';
import {
  AccountTreeRounded,
  AssignmentRounded,
  AutoAwesomeRounded,
  EditNoteRounded,
  ViewModuleRounded,
  CalendarMonthRounded,
  CategoryRounded,
  CheckCircleRounded,
  CloseRounded,
  EventAvailableRounded,
  GavelRounded,
  GradingRounded,
  MenuBookRounded,
  PictureAsPdfRounded,
  PsychologyAltRounded,
  ScheduleRounded,
  SchoolRounded,
  TuneRounded,
  ViewAgendaRounded,
} from '@mui/icons-material';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { pdf } from '@react-pdf/renderer';
import { renderWithoutHyphenation } from '../../../lib/pdf-hyphenation';
import { useSection } from '../hooks/use-portal-data';
import { PortalEmpty } from '../PortalShell';
import { useTenantStore } from '../../../stores/tenant.store';
import { useStudentIdentity } from '../../../hooks/use-student-identity';
import { courseTemplateApi, type StudentSyllabusDto } from '../../../lib/api/course-template.api';
import { loadComposedSyllabus, loadTemplate } from '../../../lib/composer/load-composed-syllabus';
import { resolveEffectiveSyllabus } from '../../../lib/composer/effective-syllabus';
import { buildSyllabusPdfLabels } from '../../../lib/syllabus-pdf-labels';
import type { SyllabusContentOverridePath } from '@dosfilos/application/composition';
import {
  buildAssignmentDistribution,
  buildWorkloadRows,
  buildCourseCalendar,
  type AssignmentTemplateType,
  type ReadingPlanRow as ReadingRow,
} from '@dosfilos/domain';
import { frameworkText, hasRichText } from '../../../lib/syllabus-text';
import { courseColor } from './CourseShell';
import {
  SyllabusPdfDocument,
  type SyllabusPdfData,
} from '../../sis/components/SyllabusPdfDocument';
import { PageViewer } from '../../sis/components/PageEditor';
import type { SyllabusEditSection as SyllabusEditKey } from '../../sis/components/SyllabusOverrideDialogs';
import { errorText } from '../../../lib/error-text';
import { formatDate } from '../../../lib/i18n-format';
import { usePortalCacheScope } from '../../../stores/portal-view.store';

function modalityLabel(t: TFunction, modality: string): string {
  const key = `portal.syllabus.modality.${modality}`;
  const translated = t(key);
  return translated === key ? modality : translated;
}

const WEEKDAY_KEY: Record<string, string> = {
  monday: 'portal.syllabus.weekdays.monday',
  tuesday: 'portal.syllabus.weekdays.tuesday',
  wednesday: 'portal.syllabus.weekdays.wednesday',
  thursday: 'portal.syllabus.weekdays.thursday',
  friday: 'portal.syllabus.weekdays.friday',
  saturday: 'portal.syllabus.weekdays.saturday',
  sunday: 'portal.syllabus.weekdays.sunday',
};

function formatDateEs(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  return formatDate(d, { day: 'numeric', month: 'short', year: 'numeric' });
}

export function CourseSyllabusPage(): JSX.Element {
  const { t } = useTranslation('sis');
  const { sectionId } = useParams<{ sectionId: string }>();
  const section = useSection(sectionId);
  const { currentTenantId } = useTenantStore();
  const { personId } = useStudentIdentity();
  const scope = usePortalCacheScope();

  const syllabusQ = useQuery({
    queryKey: ['portal-v2-syllabus', currentTenantId, scope, sectionId, personId],
    enabled: !!currentTenantId && !!sectionId,
    queryFn: () =>
      courseTemplateApi.getStudentCourseSyllabus({
        tenantId: currentTenantId!,
        sectionId: sectionId!,
        ...(personId ? { requestingStudentId: personId } : {}),
      }),
    retry: false,
  });

  if (!section) {
    return <PortalEmpty title={t('portal.syllabus.courseNotFound')} />;
  }

  return (
    <Stack spacing={2.5}>
      <SyllabusHero section={section} syllabusData={syllabusQ.data ?? null} />
      {syllabusQ.isLoading && (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress size={28} />
        </Box>
      )}
      {syllabusQ.isError && <Alert severity="info">{t('portal.syllabus.notAvailableYet')}</Alert>}
      {syllabusQ.data && (
        <SyllabusContent
          data={syllabusQ.data}
          courseName={section.courseName}
          tenantId={currentTenantId}
          clasesHref={`/portal-v2/courses/${sectionId}/clases`}
        />
      )}
    </Stack>
  );
}

// ─── Hero ────────────────────────────────────────────────────────────────────
// Tighter, more informative course card that surfaces what the student wants
// at-a-glance: course code, instructor, modality, dates, credits — all on the
// fold, no scroll.

function SyllabusHero({
  section,
  syllabusData,
}: {
  section: {
    id: string;
    courseName: string;
    courseCode: string;
    credits: number;
    facultyName?: string;
    programName?: string;
    startDate?: string | null;
    endDate?: string | null;
  };
  syllabusData: StudentSyllabusDto | null;
}): JSX.Element {
  const { t } = useTranslation('sis');
  const color = courseColor(section.id);
  const modality = syllabusData?.syllabus?.modality;
  const biblicalVersion = syllabusData?.syllabus?.biblicalVersion;
  const instructorName = syllabusData?.syllabus?.instructor.name ?? section.facultyName;
  const initials =
    instructorName
      ?.split(' ')
      .map((n) => n[0] ?? '')
      .join('')
      .slice(0, 2)
      .toUpperCase() ?? '—';

  return (
    <Card
      sx={{
        bgcolor: '#161b22',
        border: '1px solid #21262d',
        borderRadius: 2,
        overflow: 'hidden',
      }}
    >
      <Box sx={{ height: 6, bgcolor: color }} />
      <Box sx={{ p: { xs: 2.5, sm: 3 } }}>
        <Stack direction="row" spacing={1} alignItems="center" mb={1}>
          <Chip
            size="small"
            label={section.courseCode}
            sx={{
              height: 22,
              fontSize: 11,
              fontWeight: 700,
              bgcolor: color,
              color: '#fff',
            }}
          />
          <Typography variant="caption" color="#8b949e">
            {t('portal.syllabus.credits', { count: section.credits })}
          </Typography>
          {section.programName && (
            <Typography variant="caption" color="#8b949e">
              · {section.programName}
            </Typography>
          )}
        </Stack>
        <Typography
          variant="h4"
          fontWeight={700}
          color="#e6edf3"
          sx={{ fontSize: { xs: 22, sm: 28 }, lineHeight: 1.2 }}
        >
          {section.courseName}
        </Typography>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
            gap: 2,
            mt: 2.5,
          }}
        >
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Avatar sx={{ bgcolor: color, width: 36, height: 36, fontSize: 14 }}>{initials}</Avatar>
            <Box>
              <Typography variant="caption" color="#8b949e" fontWeight={700}>
                {t('portal.syllabus.hero.professor')}
              </Typography>
              <Typography variant="body2" color="#c9d1d9">
                {instructorName ?? t('portal.syllabus.hero.toBeAssigned')}
              </Typography>
            </Box>
          </Stack>
          {(section.startDate || section.endDate) && (
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Box
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: 1,
                  bgcolor: 'rgba(99,102,241,0.15)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <CalendarMonthRounded sx={{ color: '#a5b4fc', fontSize: 20 }} />
              </Box>
              <Box>
                <Typography variant="caption" color="#8b949e" fontWeight={700}>
                  {t('portal.syllabus.hero.dates')}
                </Typography>
                <Typography variant="body2" color="#c9d1d9">
                  {formatDateEs(section.startDate)}
                  {section.endDate ? ` — ${formatDateEs(section.endDate)}` : ''}
                </Typography>
              </Box>
            </Stack>
          )}
        </Box>

        {(modality || biblicalVersion) && (
          <Stack direction="row" spacing={1} mt={2} flexWrap="wrap" useFlexGap>
            {modality && (
              <Chip
                size="small"
                icon={<EventAvailableRounded sx={{ fontSize: 14 }} />}
                label={modalityLabel(t, modality)}
                variant="outlined"
                sx={{ borderColor: '#30363d', color: '#c9d1d9' }}
              />
            )}
            {biblicalVersion && (
              <Chip
                size="small"
                icon={<MenuBookRounded sx={{ fontSize: 14 }} />}
                label={t('portal.syllabus.hero.biblicalVersion', { version: biblicalVersion })}
                variant="outlined"
                sx={{ borderColor: '#30363d', color: '#c9d1d9' }}
              />
            )}
          </Stack>
        )}
      </Box>
    </Card>
  );
}

// ─── Composer integration helpers ────────────────────────────────────────────

interface InPageComposed {
  mission?: { text: string; scripture?: string };
  departmentName?: string;
  departmentPurpose?: string;
  departmentObjectives: { id: string; code?: string; text: string }[];
  programName?: string;
  programPurpose?: string;
  programObjectives: { id: string; code?: string; text: string }[];
  courseObjectives: { id: string; code?: string; text: string }[];
  description?: Record<string, unknown>;
  anchorVerse?: { reference: string; text: string; translation: string };
  biblicalVersion?: string;
  philosophy: { id: string; title: string; description: string }[];
  referencedRubrics: {
    id: string;
    title: string;
    criteria: {
      id: string;
      label: string;
      description: string;
      weight: number;
      levels?: { id: string; name: string; description: string }[];
    }[];
  }[];
}

// Entrega 1 (hueco 8): build the rendered layers from the section's FROZEN
// snapshot instead of live composition, so a later catalog edit never rewrites
// an already-created section. Used by both the in-page render and the PDF.
type SyllabusSnapshotDto = NonNullable<StudentSyllabusDto['transversalSnapshot']>;

const mapObjectives = (
  os: { id: string; code?: string; text: string }[],
): { id: string; code?: string; text: string }[] =>
  os.map((o) => ({ id: o.id, ...(o.code ? { code: o.code } : {}), text: o.text }));

const mapSnapshotRubrics = (
  rs: SyllabusSnapshotDto['rubrics'],
): InPageComposed['referencedRubrics'] =>
  rs.map((r) => ({
    id: r.id,
    title: r.title,
    criteria: r.criteria.map((cr) => ({
      id: cr.id,
      label: cr.label,
      description: cr.description,
      weight: cr.weight,
      ...(cr.levels && cr.levels.length > 0
        ? { levels: cr.levels.map((l) => ({ id: l.id, name: l.name, description: l.description })) }
        : {}),
    })),
  }));

const inPageComposedFromSnapshot = (
  snap: SyllabusSnapshotDto,
  tpl: StudentSyllabusDto['template'],
): InPageComposed => ({
  ...(snap.mission ? { mission: snap.mission } : {}),
  ...(snap.department?.name !== undefined ? { departmentName: snap.department.name } : {}),
  ...(snap.department?.purpose !== undefined ? { departmentPurpose: snap.department.purpose } : {}),
  departmentObjectives: mapObjectives(snap.department?.objectives ?? []),
  ...(snap.program?.name !== undefined ? { programName: snap.program.name } : {}),
  ...(snap.program?.purpose !== undefined ? { programPurpose: snap.program.purpose } : {}),
  programObjectives: mapObjectives(snap.program?.objectives ?? []),
  courseObjectives: mapObjectives(snap.courseObjectives),
  description: tpl.description,
  ...(tpl.anchorVerse
    ? { anchorVerse: tpl.anchorVerse as NonNullable<InPageComposed['anchorVerse']> }
    : {}),
  ...(tpl.biblicalVersion ? { biblicalVersion: tpl.biblicalVersion } : {}),
  philosophy: snap.program?.philosophy ?? [],
  referencedRubrics: mapSnapshotRubrics(snap.rubrics),
});

/** Flatten TipTap JSON to plain-text paragraphs for the in-page render. */
function tipTapToParagraphs(doc: Record<string, unknown> | undefined): string[] {
  if (!doc) return [];
  const out: string[] = [];
  const walk = (node: unknown): string => {
    if (!node || typeof node !== 'object') return '';
    const n = node as { type?: string; text?: string; content?: unknown[] };
    if (n.type === 'text' && typeof n.text === 'string') return n.text;
    if (Array.isArray(n.content)) {
      return n.content.map(walk).join('');
    }
    return '';
  };
  const root = doc as { content?: unknown[] };
  for (const node of root.content ?? []) {
    const text = walk(node).trim();
    if (text) out.push(text);
  }
  return out;
}

// Exported for reuse by the staff-facing /curso Syllabus tab (SyllabusAdminTab).
// `clasesHref` points the schedule card at the student "Clases" page; omit it
// (staff context) to hide that card — the Cronograma tab covers it there.
export function SyllabusContent({
  data,
  courseName,
  tenantId,
  clasesHref,
  scheduleHref,
  onEditSection,
}: {
  data: StudentSyllabusDto;
  courseName: string;
  tenantId: string | null | undefined;
  clasesHref?: string;
  /** Staff context: link the Módulos section to the admin Cronograma tab. */
  scheduleHref?: string;
  /** Staff context (F2): renders an edit pencil on the editable cards; the
   *  parent opens the matching override dialog. Absent (student) → read-only. */
  onEditSection?: (key: SyllabusEditKey) => void;
}): JSX.Element {
  const { t } = useTranslation('sis');
  const { gradingScale } = useTenantConfig();
  const navigate = useNavigate();
  const tpl = data.template;
  const templateId = data.template.id;
  const [composed, setComposed] = useState<InPageComposed | null>(null);

  // Entrega 1 (hueco 8): if the section froze a transversal snapshot, render it
  // verbatim and SKIP live composition — a later catalog edit must not rewrite
  // this section. Legacy sections (no snapshot) fall back to live composition.
  useEffect(() => {
    let cancelled = false;
    if (!tenantId || !templateId) return;
    if (data.transversalSnapshot) {
      setComposed(inPageComposedFromSnapshot(data.transversalSnapshot, data.template));
      return;
    }
    void (async () => {
      try {
        const tplEntity = await loadTemplate(tenantId, templateId);
        if (!tplEntity || cancelled) return;
        const c = await loadComposedSyllabus({ tenantId, template: tplEntity });
        if (cancelled) return;
        setComposed({
          ...(c.mission ? { mission: c.mission } : {}),
          ...(c.departmentName !== undefined ? { departmentName: c.departmentName } : {}),
          ...(c.departmentPurpose !== undefined ? { departmentPurpose: c.departmentPurpose } : {}),
          departmentObjectives: c.departmentObjectives.map((o) => ({
            id: o.id,
            ...(o.code ? { code: o.code } : {}),
            text: o.text,
          })),
          ...(c.programName !== undefined ? { programName: c.programName } : {}),
          ...(c.programPurpose !== undefined ? { programPurpose: c.programPurpose } : {}),
          programObjectives: c.programObjectives.map((o) => ({
            id: o.id,
            ...(o.code ? { code: o.code } : {}),
            text: o.text,
          })),
          courseObjectives: c.courseObjectives.map((o) => ({
            id: o.id,
            ...(o.code ? { code: o.code } : {}),
            text: o.text,
          })),
          description: c.description,
          ...(c.anchorVerse ? { anchorVerse: c.anchorVerse } : {}),
          ...(c.biblicalVersion ? { biblicalVersion: c.biblicalVersion } : {}),
          philosophy: c.philosophy,
          referencedRubrics: c.referencedRubrics.map((r) => ({
            id: r.id,
            title: r.title,
            criteria: r.criteria.map((cr) => ({
              id: cr.id,
              label: cr.label,
              description: cr.description,
              weight: cr.weight,
              ...(cr.levels && cr.levels.length > 0
                ? {
                    levels: cr.levels.map((l) => ({
                      id: l.id,
                      name: l.name,
                      description: l.description,
                    })),
                  }
                : {}),
            })),
          })),
        });
      } catch {
        // Composer failed — DTO fallback covers it.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [tenantId, templateId, data.transversalSnapshot, data.template]);

  // F2: per-section overrides win over composed and template for the
  // whitelisted fields — effective = override ?? composed ?? template.
  // Resolved by `resolveEffectiveSyllabus` so this view and the PDF below go
  // through the SAME precedence; they used to each apply it by hand.
  const {
    template: tplEff,
    composed: composedEff,
    overridden,
  } = resolveEffectiveSyllabus({
    template: tpl,
    composed,
    overrides: data.sectionOverrides as Record<string, unknown> | undefined,
  });
  // Header for the 4 staff-editable cards: pencil (staff only) + "edited in
  // this section" chip when any of the card's paths is overridden.
  const editableHeader = (
    labelKey: string,
    editKey: SyllabusEditKey,
    paths: SyllabusContentOverridePath[],
  ): JSX.Element => (
    <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap">
      <Stack direction="row" alignItems="center" gap={1}>
        <Typography variant="overline" color="#8b949e" fontWeight={700}>
          {t(labelKey)}
        </Typography>
        {onEditSection && paths.some((p) => overridden.has(p)) && (
          <Chip
            size="small"
            variant="outlined"
            color="info"
            sx={{ height: 20 }}
            label={t('portal.syllabus.overriddenChip')}
          />
        )}
      </Stack>
      {onEditSection && (
        <IconButton
          size="small"
          onClick={() => onEditSection(editKey)}
          aria-label={t('portal.syllabus.editSection')}
        >
          <EditNoteRounded fontSize="small" />
        </IconButton>
      )}
    </Stack>
  );
  const objectives = tpl.objectives as {
    program?: { id: string; text: string }[];
    department?: { id: string; text: string }[];
    course?: { id: string; text: string }[];
  };
  // Effective: a section may rewrite (or remove) the description of an
  // evaluation — never its weight, which the override path does not carry.
  const grading = tplEff.grading as {
    components?: {
      id: string;
      name: string;
      weight: number;
      description?: Record<string, unknown>;
      objectiveIds?: string[];
      hours?: number;
    }[];
    passingGrade?: number;
    expectedHours?: unknown;
  };
  // Normalize legacy fixed-shape hours → dynamic list (raw template may be old).
  // Mismo constructor que usa el PDF: el alumno que compare la pantalla con el
  // documento descargado no puede encontrar dos totales distintos.
  const workload = buildWorkloadRows(grading.components, grading.expectedHours);
  const readings = tplEff.readings as {
    required?: { textbookId: string; notes?: string }[];
    complementary?:
      | { intro?: string; items: { textbookId: string; notes?: string }[] }
      | { textbookId: string }[];
  };
  const requiredList = Array.isArray(readings.required) ? readings.required : [];
  const complementaryItems = Array.isArray(readings.complementary)
    ? (readings.complementary as { textbookId: string; notes?: string }[])
    : ((readings.complementary as { items?: { textbookId: string; notes?: string }[] })?.items ??
      []);
  const complementaryIntro = Array.isArray(readings.complementary)
    ? undefined
    : (readings.complementary as { intro?: string })?.intro;
  const philosophy = (tpl.philosophy ?? []) as {
    id: string;
    title: string;
    description: string;
  }[];
  const modules = (tpl.modules ?? []) as {
    id: string;
    name: string;
    axisTopic?: string;
  }[];
  // The domain union for `type`, not a local `string`: the calendar builder
  // narrows on it, and a third restatement of this shape is how the surfaces drift.
  const assignments = (tpl.assignments ?? []) as {
    id: string;
    type: AssignmentTemplateType;
    name: string;
    weekOffset: number;
    weight?: number;
  }[];
  // "Se dicta" summary per module (template-module id → "12-09-2026 – 13-09-2026").
  // Day-by-day sessions still live only in Clases/Cronograma; this is the
  // syllabus-level outline.
  const moduleDateRanges = new Map<string, string>();
  for (const g of data.moduleSchedule ?? []) {
    const dates = g.sessions.map((s) => s.date).sort();
    if (dates.length === 0) continue;
    const first = formatDateEs(dates[0]);
    const last = formatDateEs(dates[dates.length - 1]);
    moduleDateRanges.set(g.moduleId, first === last ? first : `${first} – ${last}`);
  }
  const deadlinesSafe = data.deadlines ?? [];
  // Same merge the PDF prints, from the same domain builder.
  //
  // Deliverables come from `deadlines` — the section's LIVE Assignment records —
  // not from the template list: an assignment added to the section afterwards is
  // absent from the template, one renamed there still carries the old name, and
  // one deleted there would still be listed. Dates travel WITH each deliverable,
  // because two submissions in one week can fall on different days and a single
  // date per row would have to misstate one of them.
  const effectiveReadingRows = (tplEff.readings as { plan?: { rows?: ReadingRow[] } } | undefined)
    ?.plan?.rows;
  const calendarShowsReadings = tplEff.calendarShowsReadings !== false;
  // The reading plan as a table of its own, WITHOUT weeks: when no row has a
  // week (the calendar cannot place them) or when the section asked the
  // calendar for deliveries only. Same rule as the PDF.
  const readingPlanTableRows =
    effectiveReadingRows &&
    effectiveReadingRows.length > 0 &&
    (!calendarShowsReadings || !effectiveReadingRows.some((r) => r.weekOffset !== undefined))
      ? effectiveReadingRows
      : [];
  const calendarWeeks = (() => {
    const fromDeadlines = deadlinesSafe.map((d, i) => ({
      id: `dl-${i}`,
      name: d.date && d.date !== '—' ? `${d.entry} — ${d.date}` : d.entry,
      type: 'other' as AssignmentTemplateType,
      weekOffset: d.week - 1,
    }));
    return buildCourseCalendar(
      // The EFFECTIVE readings (a section's edit must reach the calendar, not only
      // the readings card), and none at all when this section keeps the calendar
      // to deliveries — the reading plan then gets its own table below.
      calendarShowsReadings ? effectiveReadingRows : undefined,
      fromDeadlines.length > 0 ? fromDeadlines : assignments,
      // A reading with no week is already listed in the load section; an
      // assignment with no week has nowhere else to appear, so its bucket stays.
    ).filter((w) => w.weekOffset !== null || w.assignments.length > 0);
  })();
  const calendarLines = (w: (typeof calendarWeeks)[number]): string =>
    [
      ...w.readings.map(
        (r) =>
          `• ${r.label}${r.pages ? ` ${t('syllabusPdf.pagesShort', { pages: r.pages })}` : ''}`,
      ),
      ...w.assignments.map((a) => a.name),
    ].join('\n');
  const anchor =
    composed?.anchorVerse ??
    (tpl.anchorVerse as { reference: string; text: string; translation: string } | undefined);

  // Composer-preferred fields with DTO fallbacks (mirrors PDF logic).
  const effectiveProgramOAPs =
    composed && composed.programObjectives.length > 0
      ? composed.programObjectives
      : ((objectives.program ?? []) as { id: string; code?: string; text: string }[]);
  const effectiveDeptOADs =
    composed && composed.departmentObjectives.length > 0
      ? composed.departmentObjectives
      : ((objectives.department ?? []) as { id: string; code?: string; text: string }[]);
  // An override to an EMPTY list is still the teacher's answer, so ask
  // `overridden` rather than inferring intent from a zero length.
  const effectiveCourseOACs = overridden.has('objectives.course')
    ? (tplEff.objectives.course as { id: string; code?: string; text: string }[])
    : composedEff && composedEff.courseObjectives.length > 0
      ? composedEff.courseObjectives
      : ((objectives.course ?? []) as { id: string; code?: string; text: string }[]);
  const effectivePhilosophy =
    composed && composed.philosophy.length > 0
      ? composed.philosophy
      : (philosophy as { id: string; title: string; description: string }[]);
  const effectiveDescriptionParas = tipTapToParagraphs(
    composedEff?.description ?? (tplEff.description as Record<string, unknown>),
  );
  const effectiveLatePolicy = tplEff.latePolicy as
    | {
        firstWindowHours: number;
        firstWindowPenalty: number;
        secondWindowHours: number;
        secondWindowPenalty: number;
        dailyPenaltyAfter: number;
      }
    | undefined;
  const effectivePoliciesNote = tplEff.policiesNote as string | undefined;
  const effectiveGradingNote = (tplEff.grading as { note?: Record<string, unknown> } | undefined)
    ?.note;
  // An intro emptied in this section is an empty doc: treat it as absent.
  const effectiveAssignmentsNote = hasRichText(tplEff.assignmentsNote)
    ? (tplEff.assignmentsNote as Record<string, unknown>)
    : undefined;
  // Las descripciones cuelgan de la evaluación, que es la misma que aparece con
  // su peso en la tabla de calificaciones: el alumno puede seguir "Ensayo de
  // investigación" desde el 35 % hasta su descripción, su fila en la matriz y su
  // entrega en el calendario.
  const evaluations = grading.components ?? [];
  const describedEvaluations = evaluations.filter((c) => hasRichText(c.description));
  const distribution = buildAssignmentDistribution(evaluations, effectiveCourseOACs);
  const hasDistribution =
    distribution.some((r) => r.marks.some(Boolean)) && effectiveCourseOACs.length > 0;
  const effectiveSessionFormat = tpl.sessionFormat as
    | {
        weekdayTemplates: {
          weekday: string;
          startTime: string;
          endTime: string;
          segments: {
            kind: 'section' | 'break';
            durationMinutes: number;
            label?: string;
          }[];
        }[];
        introNote?: string;
      }
    | undefined;
  const effectiveReadingsPlan = (tplEff.readings as { plan?: { description?: string } } | undefined)
    ?.plan;

  const tbMap = new Map(data.textbooks.map((tb) => [tb.id, tb]));
  const fmtBook = (b: { title: string; authors: string[]; publisher?: string; year?: number }) =>
    `${b.authors.join(', ')}. ${b.title}${b.publisher ? `, ${b.publisher}` : ''}${b.year ? `, ${b.year}` : ''}.`;

  // Focus mode: when a TOC item is selected, render only that section. Click
  // "Todas las secciones" (or the close button on the focus banner) to return
  // to the full document view.
  const [focusedSection, setFocusedSection] = useState<string | null>(null);
  const isVisible = (id: string) => focusedSection === null || focusedSection === id;
  const handleFocus = (id: string | null) => {
    setFocusedSection(id);
    // Don't yank the scroll to the top — keep the user where they are. When
    // entering focus mode the chosen section is the only thing rendered, so
    // we scroll it into view smoothly on the next tick (after React commits
    // the DOM mutation). When clearing focus (id === null), leave scroll
    // alone; the user knows where they were.
    if (id && typeof window !== 'undefined') {
      requestAnimationFrame(() => {
        const el = document.getElementById(id);
        el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    }
  };

  // Build TOC entries only for sections that have content. Keeps the sidebar
  // in sync with what's actually rendered.
  const hasMission = !!composed?.mission?.text;
  const hasDept = !!(
    composed?.departmentName ||
    composed?.departmentPurpose ||
    effectiveDeptOADs.length > 0
  );
  const hasProg = !!(
    composed?.programName ||
    composed?.programPurpose ||
    effectiveProgramOAPs.length > 0
  );
  const hasDescription = effectiveDescriptionParas.length > 0 || effectiveCourseOACs.length > 0;
  const hasGrading = !!(grading.components && grading.components.length > 0);
  const hasRubrics = !!(composed && composed.referencedRubrics.length > 0);
  const hasReadings =
    requiredList.length > 0 ||
    complementaryItems.length > 0 ||
    !!effectiveReadingsPlan?.description;
  const hasSessionFormat = !!(
    effectiveSessionFormat && effectiveSessionFormat.weekdayTemplates.length > 0
  );
  const hasDeadlines = calendarWeeks.length > 0;
  // Los detalles y la matriz sobreviven a que se borre la última tarea: describen
  // la evaluación, no sus instancias. Por eso su visibilidad no depende del
  // calendario — y el PDF las imprime con la misma regla.
  const hasAssignmentDetails =
    !!effectiveAssignmentsNote ||
    describedEvaluations.length > 0 ||
    hasDistribution ||
    // Staff who emptied it in this section still need the card — and its pencil
    // — to put something back.
    (!!onEditSection &&
      (overridden.has('assignmentsNote') || overridden.has('grading.descriptions')));
  // The grade is computed from each TASK's weight, not from this table. When the
  // two stop agreeing — a weight edited by hand in Tareas, a task added later —
  // the document says one thing and the grade book computes another. Staff see
  // it; the table's own save puts them back in step.
  const weightsMismatch = (() => {
    const tasks = data.taskWeights ?? [];
    if (tasks.length === 0) return false;
    const byComponent = new Map<string, number>();
    for (const t of tasks) {
      if (!t.gradeComponentId) continue;
      byComponent.set(t.gradeComponentId, (byComponent.get(t.gradeComponentId) ?? 0) + t.weight);
    }
    const known = new Set((grading.components ?? []).map((c) => c.id));
    for (const [id, sum] of byComponent) {
      if (!known.has(id) && sum > 0) return true; // weight on an evaluation the table dropped
    }
    return (grading.components ?? []).some((c) => {
      const sum = byComponent.get(c.id);
      return sum !== undefined && Math.abs(sum - c.weight) > 0.05;
    });
  })();
  const hasPhilosophy = effectivePhilosophy.length > 0;
  const hasAnchor = !!anchor;
  const hasModules = modules.length > 0;

  const tocItems: { id: string; label: string; icon: JSX.Element }[] = [
    ...(hasAnchor
      ? [
          {
            id: 'sec-anchor',
            label: t('portal.syllabus.toc.anchor'),
            icon: <AutoAwesomeRounded sx={{ fontSize: 14 }} />,
          },
        ]
      : []),
    ...(hasMission
      ? [
          {
            id: 'sec-mission',
            label: t('portal.syllabus.toc.mission'),
            icon: <SchoolRounded sx={{ fontSize: 14 }} />,
          },
        ]
      : []),
    ...(hasDept
      ? [
          {
            id: 'sec-department',
            label: t('portal.syllabus.toc.department'),
            icon: <CategoryRounded sx={{ fontSize: 14 }} />,
          },
        ]
      : []),
    ...(hasProg
      ? [
          {
            id: 'sec-program',
            label: t('portal.syllabus.toc.program'),
            icon: <AccountTreeRounded sx={{ fontSize: 14 }} />,
          },
        ]
      : []),
    ...(hasDescription
      ? [
          {
            id: 'sec-description',
            label: t('portal.syllabus.toc.description'),
            icon: <CheckCircleRounded sx={{ fontSize: 14 }} />,
          },
        ]
      : []),
    ...(hasPhilosophy
      ? [
          {
            id: 'sec-philosophy',
            label: t('portal.syllabus.toc.philosophy'),
            icon: <PsychologyAltRounded sx={{ fontSize: 14 }} />,
          },
        ]
      : []),
    ...(hasReadings
      ? [
          {
            id: 'sec-readings',
            label: t('portal.syllabus.toc.readings'),
            icon: <MenuBookRounded sx={{ fontSize: 14 }} />,
          },
        ]
      : []),
    ...(hasSessionFormat
      ? [
          {
            id: 'sec-format',
            label: t('portal.syllabus.toc.format'),
            icon: <ScheduleRounded sx={{ fontSize: 14 }} />,
          },
        ]
      : []),
    ...(hasModules
      ? [
          {
            id: 'sec-modules',
            label: t('portal.syllabus.toc.modules', { defaultValue: 'Módulos' }),
            icon: <ViewModuleRounded sx={{ fontSize: 14 }} />,
          },
        ]
      : []),
    ...(hasAssignmentDetails
      ? [
          {
            id: 'sec-assignment-details',
            label: t('syllabusPdf.assignmentDetails'),
            icon: <AssignmentRounded sx={{ fontSize: 14 }} />,
          },
        ]
      : []),
    ...(hasGrading
      ? [
          {
            id: 'sec-grading',
            label: t('portal.syllabus.toc.grading'),
            icon: <GradingRounded sx={{ fontSize: 14 }} />,
          },
        ]
      : []),
    ...(hasDeadlines
      ? [
          {
            id: 'sec-deadlines',
            label: t('portal.syllabus.toc.deadlines'),
            icon: <CalendarMonthRounded sx={{ fontSize: 14 }} />,
          },
        ]
      : []),
    ...(hasRubrics
      ? [
          {
            id: 'sec-rubrics',
            label: t('portal.syllabus.toc.rubrics'),
            icon: <TuneRounded sx={{ fontSize: 14 }} />,
          },
        ]
      : []),
    {
      id: 'sec-policies',
      label: t('portal.syllabus.toc.policies'),
      icon: <GavelRounded sx={{ fontSize: 14 }} />,
    },
  ];

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 1fr) 220px' },
        gap: 3,
        alignItems: 'flex-start',
      }}
    >
      <Stack spacing={2.5} sx={{ minWidth: 0 }}>
        <PdfDownloadCard data={data} courseName={courseName} tenantId={tenantId} />

        {focusedSection !== null && (
          <Card
            sx={{
              bgcolor: 'rgba(99,102,241,0.08)',
              border: '1px solid rgba(99,102,241,0.35)',
              borderRadius: 2,
              p: 1.5,
            }}
          >
            <Stack direction="row" spacing={1.5} alignItems="center">
              <ViewAgendaRounded sx={{ color: '#a5b4fc', fontSize: 20 }} />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="caption" color="#a5b4fc" fontWeight={700}>
                  {t('portal.syllabus.focus.showingOnly')}
                </Typography>
                <Typography variant="body2" color="#e6edf3" fontWeight={600} noWrap>
                  {tocItems.find((it) => it.id === focusedSection)?.label ?? focusedSection}
                </Typography>
              </Box>
              <Button
                size="small"
                startIcon={<CloseRounded sx={{ fontSize: 16 }} />}
                onClick={() => handleFocus(null)}
                sx={{
                  textTransform: 'none',
                  color: '#a5b4fc',
                  '&:hover': { bgcolor: 'rgba(99,102,241,0.15)' },
                }}
              >
                {t('portal.syllabus.focus.viewAll')}
              </Button>
            </Stack>
          </Card>
        )}

        {anchor && isVisible('sec-anchor') && (
          <Card id="sec-anchor" sx={{ ...cardSx, scrollMarginTop: 24 }}>
            <Typography variant="overline" color="#a5b4fc" fontWeight={700}>
              {t('portal.syllabus.sections.anchor')} · {anchor.translation}
            </Typography>
            <Typography variant="body1" color="#e6edf3" fontStyle="italic" mt={1}>
              "{anchor.text}"
            </Typography>
            <Typography variant="caption" color="#8b949e" mt={1} display="block">
              — {anchor.reference}
            </Typography>
          </Card>
        )}

        {hasMission && isVisible('sec-mission') && composed?.mission && (
          <Card id="sec-mission" sx={{ ...cardSx, scrollMarginTop: 24 }}>
            <Typography variant="overline" color="#8b949e" fontWeight={700}>
              {t('portal.syllabus.sections.mission')}
              {composed.mission.scripture ? ` (${composed.mission.scripture})` : ''}
            </Typography>
            <Typography variant="body2" color="#c9d1d9" sx={{ whiteSpace: 'pre-wrap', mt: 1.5 }}>
              {composed.mission.text}
            </Typography>
          </Card>
        )}

        {hasDept && isVisible('sec-department') && (
          <Card id="sec-department" sx={{ ...cardSx, scrollMarginTop: 24 }}>
            <Typography variant="overline" color="#8b949e" fontWeight={700}>
              {t('portal.syllabus.sections.department')}
              {composed?.departmentName ? ` · ${composed.departmentName}` : ''}
            </Typography>
            {frameworkText(composed?.departmentPurpose) && (
              <Typography variant="body2" color="#c9d1d9" sx={{ whiteSpace: 'pre-wrap', mt: 1.5 }}>
                {frameworkText(composed?.departmentPurpose)}
              </Typography>
            )}
            {effectiveDeptOADs.length > 0 && (
              <Box mt={1.5}>
                <Typography variant="caption" color="#8b949e" fontWeight={700}>
                  {t('portal.syllabus.labels.deptObjectives')}
                </Typography>
                <Box component="ul" sx={{ pl: 3, m: 0, mt: 0.5 }}>
                  {effectiveDeptOADs.map((o) => (
                    <li key={o.id}>
                      <Typography variant="body2" color="#c9d1d9">
                        {o.code ? `${o.code}. ` : ''}
                        {o.text}
                      </Typography>
                    </li>
                  ))}
                </Box>
              </Box>
            )}
          </Card>
        )}

        {hasProg && isVisible('sec-program') && (
          <Card id="sec-program" sx={{ ...cardSx, scrollMarginTop: 24 }}>
            <Typography variant="overline" color="#8b949e" fontWeight={700}>
              {t('portal.syllabus.sections.program')}
              {composed?.programName ? ` · ${composed.programName}` : ''}
            </Typography>
            {frameworkText(composed?.programPurpose) && (
              <Typography variant="body2" color="#c9d1d9" sx={{ whiteSpace: 'pre-wrap', mt: 1.5 }}>
                {frameworkText(composed?.programPurpose)}
              </Typography>
            )}
            {effectiveProgramOAPs.length > 0 && (
              <Box mt={1.5}>
                <Typography variant="caption" color="#8b949e" fontWeight={700}>
                  {t('portal.syllabus.labels.programObjectives')}
                </Typography>
                <Box component="ul" sx={{ pl: 3, m: 0, mt: 0.5 }}>
                  {effectiveProgramOAPs.map((o) => (
                    <li key={o.id}>
                      <Typography variant="body2" color="#c9d1d9">
                        {o.code ? `${o.code}. ` : ''}
                        {o.text}
                      </Typography>
                    </li>
                  ))}
                </Box>
              </Box>
            )}
          </Card>
        )}

        {hasDescription && isVisible('sec-description') && (
          <Card id="sec-description" sx={{ ...cardSx, scrollMarginTop: 24 }}>
            {editableHeader('portal.syllabus.sections.description', 'description', [
              'description',
              'objectives.course',
            ])}
            {effectiveDescriptionParas.length > 0 && (
              <Stack spacing={1} mt={1.5}>
                {effectiveDescriptionParas.map((p, i) => (
                  <Typography key={i} variant="body2" color="#c9d1d9">
                    {p}
                  </Typography>
                ))}
              </Stack>
            )}
            {effectiveCourseOACs.length > 0 && (
              <Box mt={1.5}>
                <Typography variant="caption" color="#8b949e" fontWeight={700}>
                  {t('portal.syllabus.labels.courseObjectives')}
                </Typography>
                <Box component="ul" sx={{ pl: 3, m: 0, mt: 0.5 }}>
                  {effectiveCourseOACs.map((o) => (
                    <li key={o.id}>
                      <Typography variant="body2" color="#c9d1d9">
                        {o.code ? `${o.code}. ` : ''}
                        {o.text}
                      </Typography>
                    </li>
                  ))}
                </Box>
              </Box>
            )}
          </Card>
        )}

        {hasPhilosophy && isVisible('sec-philosophy') && (
          <Card id="sec-philosophy" sx={{ ...cardSx, scrollMarginTop: 24 }}>
            <Typography variant="overline" color="#8b949e" fontWeight={700}>
              {t('portal.syllabus.sections.philosophy')}
            </Typography>
            <Stack spacing={1.5} mt={1}>
              {effectivePhilosophy.map((p) => (
                <Box key={p.id}>
                  <Typography variant="body2" fontWeight={600} color="#e6edf3">
                    {p.title}
                  </Typography>
                  <Typography variant="body2" color="#c9d1d9" sx={{ whiteSpace: 'pre-wrap' }}>
                    {p.description}
                  </Typography>
                </Box>
              ))}
            </Stack>
          </Card>
        )}

        {(requiredList.length > 0 || complementaryItems.length > 0) &&
          isVisible('sec-readings') && (
            <Card id="sec-readings" sx={{ ...cardSx, scrollMarginTop: 24 }}>
              {editableHeader('portal.syllabus.sections.readings', 'readings', ['readings'])}
              {effectiveReadingsPlan?.description && (
                <Typography variant="body2" color="#c9d1d9" sx={{ mt: 1, whiteSpace: 'pre-wrap' }}>
                  {effectiveReadingsPlan.description}
                </Typography>
              )}
              {requiredList.length > 0 && (
                <Box mt={1}>
                  <Typography variant="body2" fontWeight={600} color="#e6edf3" mb={0.5}>
                    {t('portal.syllabus.labels.required')}
                  </Typography>
                  <Box component="ul" sx={{ pl: 3, m: 0 }}>
                    {requiredList.map((r, i) => {
                      const b = tbMap.get(r.textbookId);
                      return (
                        <li key={i}>
                          <Typography variant="body2" color="#c9d1d9">
                            {b ? fmtBook(b) : t('portal.syllabus.bookUnavailable')}
                          </Typography>
                        </li>
                      );
                    })}
                  </Box>
                </Box>
              )}
              {complementaryItems.length > 0 && (
                <Box mt={2}>
                  <Typography variant="body2" fontWeight={600} color="#e6edf3" mb={0.5}>
                    {t('portal.syllabus.labels.complementary')}
                  </Typography>
                  {complementaryIntro && (
                    <Typography variant="caption" color="#8b949e" display="block" mb={1}>
                      {complementaryIntro}
                    </Typography>
                  )}
                  <Box component="ul" sx={{ pl: 3, m: 0 }}>
                    {complementaryItems.map((r, i) => {
                      const b = tbMap.get(r.textbookId);
                      return (
                        <li key={i}>
                          <Typography variant="body2" color="#c9d1d9">
                            {b ? fmtBook(b) : t('portal.syllabus.bookUnavailable')}
                          </Typography>
                        </li>
                      );
                    })}
                  </Box>
                </Box>
              )}
            </Card>
          )}

        {hasSessionFormat && isVisible('sec-format') && effectiveSessionFormat && (
          <Card id="sec-format" sx={{ ...cardSx, scrollMarginTop: 24 }}>
            <Typography variant="overline" color="#8b949e" fontWeight={700}>
              {t('portal.syllabus.sections.format')}
            </Typography>
            <Typography variant="body2" color="#c9d1d9" sx={{ mt: 1, whiteSpace: 'pre-wrap' }}>
              {effectiveSessionFormat.introNote || t('portal.syllabus.formatIntroDefault')}
            </Typography>
            <Stack spacing={2} mt={1.5}>
              {effectiveSessionFormat.weekdayTemplates.map((wd) => (
                <Box key={wd.weekday}>
                  <Typography variant="body2" fontWeight={600} color="#e6edf3" mb={0.5}>
                    {WEEKDAY_KEY[wd.weekday] ? t(WEEKDAY_KEY[wd.weekday]!) : wd.weekday} ·{' '}
                    {wd.startTime} → {wd.endTime}
                  </Typography>
                  <Stack spacing={0.5}>
                    {(() => {
                      const parseClock = (s: string): number => {
                        const m = /^(\d{1,2}):(\d{2})$/.exec(s);
                        if (!m) return 0;
                        return parseInt(m[1]!, 10) * 60 + parseInt(m[2]!, 10);
                      };
                      const fmt = (n: number) =>
                        `${String(Math.floor(n / 60) % 24).padStart(2, '0')}:${String(n % 60).padStart(2, '0')}`;
                      let cursor = parseClock(wd.startTime);
                      let sec = 0;
                      let brk = 0;
                      return wd.segments.map((seg, i) => {
                        const start = cursor;
                        const end = cursor + (seg.durationMinutes || 0);
                        cursor = end;
                        if (seg.kind === 'section') sec += 1;
                        else brk += 1;
                        return (
                          <Box
                            key={i}
                            sx={{
                              display: 'flex',
                              gap: 1.5,
                              px: 1,
                              py: 0.5,
                              bgcolor:
                                seg.kind === 'break'
                                  ? 'rgba(245,158,11,0.06)'
                                  : 'rgba(99,102,241,0.06)',
                              borderRadius: 0.5,
                            }}
                          >
                            <Typography
                              variant="caption"
                              sx={{ fontFamily: 'monospace', minWidth: 120, color: '#c9d1d9' }}
                            >
                              {fmt(start)} → {fmt(end)}
                            </Typography>
                            <Typography
                              variant="caption"
                              color={seg.kind === 'break' ? '#f59e0b' : '#a5b4fc'}
                              fontWeight={600}
                            >
                              {seg.kind === 'section'
                                ? (seg.label?.trim() ??
                                  t('portal.syllabus.format.block', { number: sec }))
                                : seg.label?.trim()
                                  ? t('portal.syllabus.format.breakLabeled', { label: seg.label })
                                  : t('portal.syllabus.format.break', { number: brk })}
                            </Typography>
                            <Typography variant="caption" color="#8b949e">
                              {t('portal.syllabus.format.minutes', { count: seg.durationMinutes })}
                            </Typography>
                          </Box>
                        );
                      });
                    })()}
                  </Stack>
                </Box>
              ))}
            </Stack>
          </Card>
        )}

        {/* Temario de módulos: name + axis + "se dicta" summary. The day-by-day
            calendar still lives ONLY in Clases (student) / Cronograma (admin) —
            this is the syllabus-level outline of it. */}
        {hasModules && isVisible('sec-modules') && (
          <Card id="sec-modules" sx={{ ...cardSx, scrollMarginTop: 24 }}>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              spacing={2}
              flexWrap="wrap"
            >
              <Typography variant="overline" color="#8b949e" fontWeight={700}>
                {t('portal.syllabus.sections.modules', { defaultValue: 'Módulos' })}
              </Typography>
              {(clasesHref ?? scheduleHref) && (
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => navigate((clasesHref ?? scheduleHref)!)}
                >
                  {clasesHref
                    ? t('portal.syllabus.goToClasses', { defaultValue: 'Ver clases' })
                    : t('portal.syllabus.goToCronograma', { defaultValue: 'Ver cronograma' })}
                </Button>
              )}
            </Stack>
            <Stack spacing={1.5} mt={1.5}>
              {modules.map((m) => {
                const range = moduleDateRanges.get(m.id);
                return (
                  <Box key={m.id}>
                    <Typography variant="subtitle2" fontWeight={700} sx={{ color: '#c9d1d9' }}>
                      {m.name}
                    </Typography>
                    {m.axisTopic && m.axisTopic !== m.name && (
                      <Typography variant="body2" color="#8b949e">
                        {m.axisTopic}
                      </Typography>
                    )}
                    {range && (
                      <Typography variant="caption" color="#8b949e">
                        {t('portal.syllabus.modules.taughtOn', {
                          range,
                          defaultValue: `Se dicta: ${range}`,
                        })}
                      </Typography>
                    )}
                  </Box>
                );
              })}
            </Stack>
            {clasesHref && (
              <Typography variant="caption" color="#8b949e" display="block" mt={1.5}>
                {t('portal.syllabus.scheduleMovedToClasses', {
                  defaultValue:
                    'El calendario de clases y tu asistencia están en la pestaña Clases.',
                })}
              </Typography>
            )}
          </Card>
        )}

        {/* "Detalles de la tarea" — sección propia, como en el documento impreso.
            Antes compartía tarjeta con el calendario bajo un solo encabezado, y
            por eso la pantalla no tenía las secciones que el plan de estudio real
            tiene. Sigue viva aunque no quede ninguna tarea: describe la
            evaluación, no sus instancias. */}
        {hasAssignmentDetails && isVisible('sec-assignment-details') && (
          <Card id="sec-assignment-details" sx={{ ...cardSx, scrollMarginTop: 24 }}>
            {editableHeader('syllabusPdf.assignmentDetails', 'assignmentDetails', [
              'assignmentsNote',
              'grading.descriptions',
            ])}
            {effectiveAssignmentsNote && (
              <Box mt={1} mb={1.5} sx={{ color: '#c9d1d9' }}>
                <PageViewer value={effectiveAssignmentsNote} />
              </Box>
            )}
            {/* Mismo contenido que imprime el PDF, del mismo builder del dominio: un
                alumno que compare la pantalla con el documento descargado no puede
                encontrar dos respuestas distintas. */}
            {describedEvaluations.map((c) => (
              <Box key={c.id} mt={1.5} sx={{ color: '#c9d1d9' }}>
                <Typography variant="subtitle2" fontWeight={700} sx={{ color: '#e6edf3' }}>
                  {c.name}
                </Typography>
                <PageViewer value={c.description!} />
              </Box>
            ))}
            {hasDistribution && (
              <Box mt={2}>
                <Typography variant="overline" color="#8b949e" fontWeight={700}>
                  {t('syllabusPdf.distribution')}
                </Typography>
                {/* Its own scroller: a course with many objectives would otherwise
                    push the table past the card on a phone. */}
                <Box sx={{ overflowX: 'auto', mt: 0.5 }}>
                  <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse' }}>
                    <Box component="thead">
                      <Box component="tr">
                        <Box component="th" sx={distCellSx} />
                        {effectiveCourseOACs.map((o, i) => (
                          <Box key={o.id} component="th" sx={{ ...distCellSx, fontWeight: 700 }}>
                            {o.code || t('syllabusPdf.objectiveShort', { n: i + 1 })}
                          </Box>
                        ))}
                      </Box>
                    </Box>
                    <Box component="tbody">
                      {distribution.map((row) => (
                        <Box component="tr" key={row.componentId}>
                          <Box component="td" sx={{ ...distCellSx, textAlign: 'left' }}>
                            {row.title}
                          </Box>
                          {row.marks.map((marked, i) => (
                            <Box key={i} component="td" sx={distCellSx}>
                              {marked ? 'X' : ''}
                            </Box>
                          ))}
                        </Box>
                      ))}
                    </Box>
                  </Box>
                </Box>
              </Box>
            )}
          </Card>
        )}

        {grading.components && grading.components.length > 0 && isVisible('sec-grading') && (
          <Card id="sec-grading" sx={{ ...cardSx, scrollMarginTop: 24 }}>
            {editableHeader('portal.syllabus.sections.grading', 'grading', [
              'grading.note',
              'grading.components',
            ])}
            {onEditSection && weightsMismatch && (
              <Alert severity="warning" variant="outlined" sx={{ mt: 1 }}>
                {t('portal.syllabus.weightsMismatch')}
              </Alert>
            )}
            {effectiveGradingNote && (
              <Box mt={1} mb={1.5} sx={{ color: '#c9d1d9' }}>
                <PageViewer value={effectiveGradingNote} />
              </Box>
            )}
            <Stack spacing={1} mt={1}>
              {grading.components.map((c) => (
                <Stack key={c.id} direction="row" justifyContent="space-between">
                  <Typography variant="body2" color="#c9d1d9">
                    {c.name}
                  </Typography>
                  <Typography variant="body2" fontWeight={600} color="#e6edf3">
                    {c.weight}%
                  </Typography>
                </Stack>
              ))}
              <Divider sx={{ borderColor: '#21262d', my: 1 }} />
              <Stack direction="row" justifyContent="space-between">
                <Typography variant="body2" color="#8b949e">
                  {t('portal.syllabus.labels.passingGrade')}
                </Typography>
                <Typography variant="body2" fontWeight={700} color="#a5b4fc">
                  {/* En la escala del seminario: antes salía «4%». */}
                  {passingText(gradingScale, grading.passingGrade)}
                </Typography>
              </Stack>
              {workload.rows.length > 0 && (
                <Box mt={1}>
                  <Typography variant="caption" color="#8b949e" display="block" mb={0.5}>
                    {t('portal.syllabus.labels.estimatedLoad')}
                  </Typography>
                  <Stack direction="row" flexWrap="wrap" useFlexGap spacing={1}>
                    {workload.rows.map((r) => (
                      <Chip
                        key={`${r.graded ? 'c' : 'h'}-${r.id}`}
                        size="small"
                        label={t('portal.syllabus.load.item', {
                          label: r.label,
                          hours: r.hours,
                        })}
                        variant="outlined"
                        sx={{ borderColor: '#30363d', color: '#c9d1d9' }}
                      />
                    ))}
                    <Chip
                      size="small"
                      label={t('portal.syllabus.load.total', { hours: workload.total })}
                      sx={{ bgcolor: '#1f6feb', color: '#fff' }}
                    />
                  </Stack>
                </Box>
              )}
            </Stack>
          </Card>
        )}

        {/* "Calendario del curso": una fila por semana con qué leer y qué entregar. */}
        {hasDeadlines && isVisible('sec-deadlines') && (
          <Card id="sec-deadlines" sx={{ ...cardSx, scrollMarginTop: 24 }}>
            {editableHeader('syllabusPdf.courseCalendar', 'calendar', ['calendar.showReadings'])}
            <Box
              sx={{
                mt: 1,
                border: '1px solid #21262d',
                borderRadius: 1,
                overflow: 'hidden',
              }}
            >
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: '60px 1fr',
                  bgcolor: 'rgba(255,255,255,0.03)',
                  px: 1.25,
                  py: 0.75,
                }}
              >
                <Typography variant="caption" color="#8b949e" fontWeight={700}>
                  {t('portal.syllabus.tableHeaders.week')}
                </Typography>
                {/* The column lists readings as well as submissions now, so the
                      old "Entrega" heading would name only half of it. */}
                <Typography variant="caption" color="#8b949e" fontWeight={700}>
                  {t('syllabusPdf.col.courseWork')}
                </Typography>
              </Box>
              {calendarWeeks.map((w, i) => (
                <Box
                  key={i}
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: '60px 1fr',
                    borderTop: '1px solid #21262d',
                    px: 1.25,
                    py: 0.75,
                  }}
                >
                  <Typography variant="caption" color="#c9d1d9">
                    {w.weekOffset === null ? '—' : w.weekOffset + 1}
                  </Typography>
                  {/* `pre-line`: the builder joins a week's lines with \n, and
                        HTML would otherwise collapse them into one run-on line —
                        the PDF prints them stacked, and the two must agree. */}
                  <Typography variant="caption" color="#c9d1d9" sx={{ whiteSpace: 'pre-line' }}>
                    {calendarLines(w)}
                  </Typography>
                </Box>
              ))}
            </Box>
            {readingPlanTableRows.length > 0 && (
              <Box mt={2}>
                <Typography variant="overline" color="#8b949e" fontWeight={700}>
                  {t('portal.syllabus.readingPlan.title')}
                </Typography>
                <Box sx={{ mt: 0.5, border: '1px solid #21262d', borderRadius: 1 }}>
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: '1fr 80px',
                      bgcolor: 'rgba(255,255,255,0.03)',
                      px: 1.25,
                      py: 0.75,
                    }}
                  >
                    <Typography variant="caption" color="#8b949e" fontWeight={700}>
                      {t('portal.syllabus.readingPlan.reading')}
                    </Typography>
                    <Typography variant="caption" color="#8b949e" fontWeight={700}>
                      {t('portal.syllabus.readingPlan.pages')}
                    </Typography>
                  </Box>
                  {readingPlanTableRows.map((r, i) => (
                    <Box
                      key={r.id ?? i}
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: '1fr 80px',
                        borderTop: '1px solid #21262d',
                        px: 1.25,
                        py: 0.75,
                      }}
                    >
                      <Typography variant="caption" color="#c9d1d9">
                        {r.label}
                      </Typography>
                      <Typography variant="caption" color="#c9d1d9">
                        {r.pages ?? ''}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            )}
          </Card>
        )}

        {hasRubrics && isVisible('sec-rubrics') && composed && (
          <Card id="sec-rubrics" sx={{ ...cardSx, scrollMarginTop: 24 }}>
            <Typography variant="overline" color="#8b949e" fontWeight={700}>
              {t('portal.syllabus.sections.rubrics')}
            </Typography>
            <Stack spacing={2.5} mt={1.5}>
              {composed.referencedRubrics.map((rubric) => {
                const usesLevels = rubric.criteria.some((c) => c.levels && c.levels.length > 0);
                const levelNames = usesLevels
                  ? (rubric.criteria[0]?.levels?.map((l) => l.name) ?? [])
                  : [];
                return (
                  <Box key={rubric.id}>
                    <Typography variant="body2" fontWeight={700} color="#e6edf3" mb={1}>
                      {rubric.title}
                    </Typography>
                    <Box
                      sx={{
                        border: '1px solid #21262d',
                        borderRadius: 1,
                        overflow: 'auto',
                      }}
                    >
                      <Box
                        sx={{
                          display: 'grid',
                          gridTemplateColumns: usesLevels
                            ? `minmax(160px, 2fr) ${levelNames.map(() => 'minmax(120px, 1.5fr)').join(' ')} 60px`
                            : 'minmax(160px, 2fr) 60px',
                          bgcolor: 'rgba(255,255,255,0.03)',
                          px: 1.25,
                          py: 0.75,
                          gap: 1,
                        }}
                      >
                        <Typography variant="caption" color="#8b949e" fontWeight={700}>
                          {t('portal.syllabus.labels.criterion')}
                        </Typography>
                        {usesLevels &&
                          levelNames.map((n) => (
                            <Typography key={n} variant="caption" color="#8b949e" fontWeight={700}>
                              {n.toUpperCase()}
                            </Typography>
                          ))}
                        <Typography variant="caption" color="#8b949e" fontWeight={700}>
                          {t('portal.syllabus.labels.weight')}
                        </Typography>
                      </Box>
                      {rubric.criteria.map((c) => (
                        <Box
                          key={c.id}
                          sx={{
                            display: 'grid',
                            gridTemplateColumns: usesLevels
                              ? `minmax(160px, 2fr) ${levelNames.map(() => 'minmax(120px, 1.5fr)').join(' ')} 60px`
                              : 'minmax(160px, 2fr) 60px',
                            borderTop: '1px solid #21262d',
                            px: 1.25,
                            py: 1,
                            gap: 1,
                            alignItems: 'flex-start',
                          }}
                        >
                          <Box>
                            <Typography variant="caption" color="#e6edf3" fontWeight={700}>
                              {c.label}
                            </Typography>
                            {c.description && (
                              <Typography
                                variant="caption"
                                color="#8b949e"
                                display="block"
                                mt={0.25}
                              >
                                {c.description}
                              </Typography>
                            )}
                          </Box>
                          {usesLevels &&
                            levelNames.map((n) => {
                              const lvl = (c.levels ?? []).find((x) => x.name === n);
                              return (
                                <Typography key={n} variant="caption" color="#c9d1d9">
                                  {lvl?.description ?? '—'}
                                </Typography>
                              );
                            })}
                          <Typography variant="caption" color="#a5b4fc" fontWeight={700}>
                            {c.weight} %
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                );
              })}
            </Stack>
          </Card>
        )}

        {isVisible('sec-policies') && (
          <Card id="sec-policies" sx={{ ...cardSx, scrollMarginTop: 24 }}>
            {editableHeader('portal.syllabus.sections.policies', 'policies', [
              'policiesNote',
              'latePolicy',
            ])}
            <Typography variant="body2" color="#c9d1d9" sx={{ mt: 1, whiteSpace: 'pre-wrap' }}>
              {effectivePoliciesNote || t('portal.syllabus.defaultPoliciesNote')}
            </Typography>
            {effectiveLatePolicy && (
              <Box mt={1.5}>
                <Typography variant="caption" color="#8b949e" display="block" mb={0.5}>
                  {t('portal.syllabus.labels.latePolicy')}
                </Typography>
                <Typography variant="caption" color="#c9d1d9">
                  {t('portal.syllabus.latePolicyDetail', {
                    firstHours: effectiveLatePolicy.firstWindowHours,
                    firstPenalty: effectiveLatePolicy.firstWindowPenalty,
                    secondHours: effectiveLatePolicy.secondWindowHours,
                    secondPenalty: effectiveLatePolicy.secondWindowPenalty,
                    dailyPenalty: effectiveLatePolicy.dailyPenaltyAfter,
                  })}
                </Typography>
              </Box>
            )}
          </Card>
        )}
      </Stack>

      {/* Sticky TOC sidebar on desktop. Hidden on mobile. */}
      {tocItems.length > 0 && (
        <Box
          component="aside"
          sx={{
            display: { xs: 'none', md: 'block' },
            position: 'sticky',
            top: 24,
            alignSelf: 'flex-start',
          }}
        >
          <Card sx={{ bgcolor: '#161b22', border: '1px solid #21262d', borderRadius: 2, p: 2 }}>
            <Typography
              variant="overline"
              color="#8b949e"
              fontWeight={700}
              sx={{ letterSpacing: 0.5 }}
            >
              {t('portal.syllabus.toc.onThisPage')}
            </Typography>
            <Stack spacing={0.25} mt={1}>
              {/* "Todas las secciones" — only shown when a section is focused. */}
              {focusedSection !== null && (
                <Box
                  onClick={() => handleFocus(null)}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    px: 1,
                    py: 0.75,
                    borderRadius: 1,
                    color: '#a5b4fc',
                    cursor: 'pointer',
                    transition: 'background 0.1s, color 0.1s',
                    '&:hover': { bgcolor: 'rgba(99,102,241,0.18)' },
                  }}
                >
                  <ViewAgendaRounded sx={{ fontSize: 14 }} />
                  <Typography variant="body2" sx={{ fontSize: 13, fontWeight: 600 }}>
                    {t('portal.syllabus.toc.allSections')}
                  </Typography>
                </Box>
              )}
              {tocItems.map((it) => {
                const isSelected = focusedSection === it.id;
                return (
                  <Box
                    key={it.id}
                    onClick={() => handleFocus(it.id)}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      px: 1,
                      py: 0.75,
                      borderRadius: 1,
                      color: isSelected ? '#e6edf3' : '#8b949e',
                      bgcolor: isSelected ? 'rgba(99,102,241,0.18)' : 'transparent',
                      cursor: 'pointer',
                      transition: 'background 0.1s, color 0.1s',
                      '&:hover': { bgcolor: 'rgba(99,102,241,0.12)', color: '#e6edf3' },
                    }}
                  >
                    {it.icon}
                    <Typography
                      variant="body2"
                      sx={{ fontSize: 13, fontWeight: isSelected ? 600 : 400 }}
                    >
                      {it.label}
                    </Typography>
                  </Box>
                );
              })}
            </Stack>
          </Card>
        </Box>
      )}
    </Box>
  );
}

function PdfDownloadCard({
  data,
  courseName,
  tenantId,
}: {
  data: StudentSyllabusDto;
  courseName: string;
  tenantId: string | null | undefined;
}): JSX.Element {
  const { t, i18n } = useTranslation('sis');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Resolved here rather than inside the document: react-pdf renders it
  // outside this React tree, so it cannot reach the i18next provider.
  const labels = buildSyllabusPdfLabels(t, i18n.language);

  const handleDownload = async () => {
    setBusy(true);
    setError(null);
    try {
      const tbMap: SyllabusPdfData['textbookMap'] = {};
      for (const tb of data.textbooks) {
        tbMap[tb.id] = {
          title: tb.title,
          authors: tb.authors,
          ...(tb.year ? { year: tb.year } : {}),
          ...(tb.publisher ? { publisher: tb.publisher } : {}),
        };
      }
      const today = new Date();
      const sixtyFromNow = new Date(today.getTime() + 60 * 24 * 60 * 60 * 1000);
      const isoDate = (d: Date) => d.toISOString().slice(0, 10);
      const templateId = data.template.id;
      const tpl = data.template as unknown as SyllabusPdfData['template'];

      // Entrega 1 (hueco 8): prefer the section's frozen snapshot for the PDF;
      // a later catalog edit must not rewrite an already-created section. Legacy
      // sections (no snapshot) fall back to live composition below.
      let composed: SyllabusPdfData['composed'] | undefined;
      let referencedRubrics: SyllabusPdfData['referencedRubrics'] = undefined;
      const frozen = data.transversalSnapshot;
      if (frozen) {
        referencedRubrics =
          frozen.rubrics.length > 0 ? mapSnapshotRubrics(frozen.rubrics) : undefined;
        composed = {
          ...(frozen.program?.name !== undefined ? { programName: frozen.program.name } : {}),
          ...(frozen.program?.purpose !== undefined
            ? { programPurpose: frozen.program.purpose }
            : {}),
          ...(frozen.department?.name !== undefined
            ? { departmentName: frozen.department.name }
            : {}),
          ...(frozen.department?.purpose !== undefined
            ? { departmentPurpose: frozen.department.purpose }
            : {}),
          departmentObjectives: mapObjectives(frozen.department?.objectives ?? []),
          programObjectives: mapObjectives(frozen.program?.objectives ?? []),
          courseObjectives: mapObjectives(frozen.courseObjectives),
          description: data.template.description,
          ...(data.template.anchorVerse
            ? {
                anchorVerse: data.template.anchorVerse as NonNullable<
                  NonNullable<SyllabusPdfData['composed']>['anchorVerse']
                >,
              }
            : {}),
          ...(data.template.biblicalVersion
            ? { biblicalVersion: data.template.biblicalVersion }
            : {}),
          philosophy: frozen.program?.philosophy ?? [],
        };
      } else if (tenantId && templateId) {
        try {
          const tplEntity = await loadTemplate(tenantId, templateId);
          if (tplEntity) {
            const c = await loadComposedSyllabus({ tenantId, template: tplEntity });
            if (c.referencedRubrics && c.referencedRubrics.length > 0) {
              referencedRubrics = c.referencedRubrics.map((r) => ({
                id: r.id,
                title: r.title,
                criteria: r.criteria.map((cr) => ({
                  id: cr.id,
                  label: cr.label,
                  description: cr.description,
                  weight: cr.weight,
                  ...(cr.levels && cr.levels.length > 0
                    ? {
                        levels: cr.levels.map((l) => ({
                          id: l.id,
                          name: l.name,
                          description: l.description,
                        })),
                      }
                    : {}),
                })),
              }));
            }
            composed = {
              ...(c.programName !== undefined ? { programName: c.programName } : {}),
              ...(c.programPurpose !== undefined ? { programPurpose: c.programPurpose } : {}),
              ...(c.departmentName !== undefined ? { departmentName: c.departmentName } : {}),
              ...(c.departmentPurpose !== undefined
                ? { departmentPurpose: c.departmentPurpose }
                : {}),
              departmentObjectives: (c.departmentObjectives ?? []).map((o) => ({
                id: o.id,
                ...(o.code ? { code: o.code } : {}),
                text: o.text,
              })),
              programObjectives: (c.programObjectives ?? []).map((o) => ({
                id: o.id,
                ...(o.code ? { code: o.code } : {}),
                text: o.text,
              })),
              courseObjectives: (c.courseObjectives ?? []).map((o) => ({
                id: o.id,
                ...(o.code ? { code: o.code } : {}),
                text: o.text,
              })),
              description: c.description,
              ...(c.anchorVerse ? { anchorVerse: c.anchorVerse } : {}),
              ...(c.biblicalVersion ? { biblicalVersion: c.biblicalVersion } : {}),
              philosophy: c.philosophy,
            };
          }
        } catch (composeErr) {
          // eslint-disable-next-line no-console
          console.warn('[StudentSyllabusPdf] compose failed; falling back', composeErr);
        }
      }

      // F2: section overrides beat composed AND template in the PDF too — and
      // through the SAME resolver the on-screen view uses, so the download can
      // no longer disagree with what the page shows. `SyllabusPdfDocument`
      // prefers `composed` for description/OACs and reads the rest straight off
      // `template`, which is why the resolver returns both override-aware.
      const effective = resolveEffectiveSyllabus({
        template: tpl,
        composed,
        overrides: data.sectionOverrides as Record<string, unknown> | undefined,
      });
      const tplEffective = effective.template;
      composed = effective.composed;

      const pdfData: SyllabusPdfData = {
        template: tplEffective,
        syllabus: data.syllabus ?? {
          instructor: { name: t('portal.syllabus.instructorToBeAssigned') },
          termRange: { startDate: isoDate(today), endDate: isoDate(sixtyFromNow) },
          modality: 'online',
        },
        textbookMap: tbMap,
        moduleSchedule:
          data.moduleSchedule && data.moduleSchedule.length > 0
            ? data.moduleSchedule
            : tpl.modules.map((m) => ({ moduleId: m.id, sessions: [] })),
        deadlines:
          data.deadlines && data.deadlines.length > 0
            ? data.deadlines
            : [...tpl.assignments]
                .sort((a, b) => a.weekOffset - b.weekOffset)
                .map((a) => ({
                  week: a.weekOffset + 1,
                  date: '—',
                  entry: a.name,
                })),
        tenantName: data.tenantName,
        ...(data.documentLogoUrl ? { documentLogoUrl: data.documentLogoUrl } : {}),
        ...(data.mission ? { mission: data.mission } : {}),
        ...(data.programName ? { programName: data.programName } : {}),
        ...(composed ? { composed } : {}),
        ...(referencedRubrics && referencedRubrics.length > 0 ? { referencedRubrics } : {}),
      };
      // Without hyphenation: @react-pdf splits Spanish words with English rules.
      const blob = await renderWithoutHyphenation(() =>
        pdf(<SyllabusPdfDocument data={pdfData} labels={labels} />).toBlob(),
      );
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Plan-${courseName.replace(/\s+/g, '-')}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError(errorText(e, t('portal.syllabus.pdf.error')));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card
      sx={{
        bgcolor: 'rgba(99,102,241,0.06)',
        border: '1px solid rgba(99,102,241,0.4)',
        borderRadius: 2,
        p: 2.5,
      }}
    >
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }}>
        <Box sx={{ flex: 1 }}>
          <Typography variant="subtitle1" fontWeight={700} color="#e6edf3">
            {t('portal.syllabus.pdf.title')}
          </Typography>
          <Typography variant="caption" color="#8b949e">
            {t('portal.syllabus.pdf.description')}
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={
            busy ? <CircularProgress size={16} color="inherit" /> : <PictureAsPdfRounded />
          }
          onClick={handleDownload}
          disabled={busy}
        >
          {busy ? t('portal.syllabus.pdf.generating') : t('portal.syllabus.pdf.download')}
        </Button>
      </Stack>
      {error && (
        <Alert severity="error" sx={{ mt: 1.5 }}>
          {error}
        </Alert>
      )}
    </Card>
  );
}

/** Cells of the assignment-distribution matrix — centred marks, thin grid. */
const distCellSx = {
  border: '1px solid #21262d',
  padding: '4px 8px',
  textAlign: 'center' as const,
  fontSize: 13,
  color: '#c9d1d9',
};

const cardSx = {
  bgcolor: '#161b22',
  border: '1px solid #21262d',
  borderRadius: 2,
  p: 2.5,
} as const;
